import { useEffect, useState } from 'react';
import { api } from '../api/client';

export const OBJECTIVE_STATES = [
  ['a_travailler', 'À travailler'], ['en_developpement', 'En développement'], ['atteint', 'Atteint'],
] as const;
export interface Objective {
  topic_id: number; name: string; color: string; status: string | null;
  explanation: string; status_date: string | null; author: string | null;
  context: string | null; defi: string | null; horizon: string | null; bsp_horizon: string | null;
  history: { status: string | null; date: string; explanation: string; author: string }[];
}
export function ObjectiveStatus({ spId, topicId, objective, onSaved }: {
  spId: number; topicId: number; objective?: Objective; onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const save = async (status: string | null) => {
    const explanation = window.prompt('Observation sur cet objectif (facultatif)', '');
    if (explanation == null) return;
    setBusy(true); setError('');
    try { await api.post(`/api/quality/objectives/${spId}/${topicId}`, { status, explanation }); onSaved(); }
    catch (e) { setError(String(e)); } finally { setBusy(false); }
  };
  return <div className="objective-status">
    <div className="objective-status-buttons" aria-label="Avancement de l’objectif">
      {OBJECTIVE_STATES.map(([v,label]) => <button type="button" key={v} disabled={busy}
        className={`btn btn-sm ${objective?.status===v ? 'btn-accent' : 'btn-ghost'}`}
        aria-pressed={objective?.status===v} onClick={() => save(v)}>{label}</button>)}
      {objective?.status && <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => save(null)}>Non évalué</button>}
    </div>
    {objective?.status_date && <small>{objective.status_date} — {objective.author}{objective.explanation ? ` : ${objective.explanation}` : ''}</small>}
    {error && <div role="alert">{error}</div>}
  </div>;
}
export function useObjectives(participantId: number | null, date?: string, revision?: string) {
  const [objectives, setObjectives] = useState<Objective[]>([]), [error,setError] = useState('');
  const [message,setMessage] = useState('');
  const [serial,setSerial] = useState(0);
  useEffect(() => {
    let alive = true; setObjectives([]); setError(''); setMessage('');
    if (!participantId) return;
    const timer = setTimeout(() => { api.get<{ objectives: Objective[]; message?: string }>(`/api/quality/objectives/${participantId}${date ? `?date=${date}` : ''}`)
      .then(r => { if (alive) { setObjectives(r.objectives); setMessage(r.message ?? ''); } })
      .catch(e => { if (alive) setError(String(e)); }); }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [participantId,date,revision,serial]);
  return { objectives, error, message, refresh: () => setSerial(x => x+1) };
}
export function ObjectiveFollowup({ objectives, message, error }: { objectives: Objective[]; message?: string; error?: string }) {
  const [shortOnly,setShortOnly] = useState(false);
  const horizons: Record<string,string> = { court: 'Court terme', moyen: 'Moyen terme', long: 'Long terme' };
  const sorted = [...objectives].sort((a,b) => (a.bsp_horizon==='court' ? 0 : 1)-(b.bsp_horizon==='court' ? 0 : 1));
  return <section className="objective-followup">
    <div className="objective-followup-head"><h3>Suivi des objectifs</h3>
      <label><input type="checkbox" checked={shortOnly} onChange={e => setShortOnly(e.target.checked)} /> Priorités à court terme du BSP</label>
    </div>
    <p className="save-indicator">Les horizons sont des priorités de travail, sans durée imposée. L’état est évalué par l’intervenant.</p>
    {error && <p role="alert">{error}</p>}{message && <p>{message}</p>}
    <div className="objective-table-wrap"><table className="table"><thead><tr><th>Carte</th><th>Priorité BSP</th><th>État</th><th>Observation</th><th>Historique</th></tr></thead>
      <tbody>{sorted.filter(o => !shortOnly || o.bsp_horizon==='court').map(o => <tr key={o.topic_id}>
        <td><span className="pickmeta-dot" style={{background:o.color}} /> {o.name}</td>
        <td>{horizons[o.bsp_horizon ?? ''] ?? '—'}</td>
        <td>{OBJECTIVE_STATES.find(([v]) => v===o.status)?.[1] ?? 'Non évalué'}</td>
        <td>{o.context || '—'}{o.defi && <div><small>Défi de la séance : {o.defi}</small></div>}</td>
        <td>{o.history.length>0 ? <details><summary>{o.history.length} évaluation{o.history.length>1?'s':''}</summary>
          {o.history.map((e,i) => <p key={i}>{e.date} — {e.author}<br />{OBJECTIVE_STATES.find(([v])=>v===e.status)?.[1] ?? 'Non évalué'}{e.explanation && ` : ${e.explanation}`}</p>)}</details> : '—'}</td>
      </tr>)}</tbody></table></div>
    {!objectives.length && !message && !error && <p className="save-indicator">Aucune carte enregistrée dans cette mesure.</p>}
  </section>;
}
